/**
 * /teaching-plans — single-page Teaching Plan workspace.
 *
 * Three sections, top to bottom:
 *
 *   1. Curriculum agenda — separate widget for uploading / replacing
 *      the teacher's semester syllabus (lives in /documents/curricula/).
 *
 *   2. Generate a new plan — Week + Topic + CustomPromptInput (with
 *      the existing 💡 AI suggest button) + Generate button. Mirrors
 *      the old Extensions form so teachers don't have to relearn the
 *      flow. After generate, the 4-section materials render inline
 *      below the form, and the same content is auto-saved as Markdown
 *      to /documents/teaching-plans/week-NN_<slug>.md.
 *
 *   3. My plans — list of saved plans (week + topic), with view /
 *      edit / delete actions. Editing routes to the dedicated
 *      TeachingPlanEditPage for the deep-link experience.
 */
import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authHeaders } from "../../api/school";
import { useTranslation } from "react-i18next";

import { MathText } from "../../components/MathText";

import { useMyEmployees } from "../workbench/EmployeeSwitcher";

import {
  useClasses,
  useCurriculumUnits,
  useGenerateTeachingPlan,
  type TeachingPlan,
  useKnowledgePoints,
  useSubjects,
} from "../../api/school";
import { CustomPromptInput } from "../../components/CustomPromptInput";
import {
  CURRICULA_PATH,
  PLANS_PATH,
  curriculaPathFor,
  deleteDoc,
  listDocs,
  readDocTextForPrompt,
  sortFilesByTimeDesc,
  uploadDoc,
  type DocTreeFile,
} from "./documentsApi";
import {
  parsePlanFilename,
  planFilename,
  serializeTeachingPlanV2,
} from "./serializeMarkdown";

// html/mhtml 也是文本课纲(AI 生成的汇报/大纲现在就是 HTML) —— 读取时
// 走 readDocTextForPrompt 剥标签,不再当二进制跳过。
export const TEXTUAL_EXTS = /\.(md|markdown|txt|html?|mhtml?)$/i;

export function TeachingPlansPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          {t("teachingPlans.form.title")}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          {t("teachingPlans.form.subtitle")}
        </p>
      </header>
      <AgendaSection />
      <GenerateSection />
      <PlansListSection />
    </div>
  );
}

// ── 1. Agenda section ────────────────────────────────────────────────

/** 取某学科的大纲:先看 curricula/<slug>/,空了回落到平铺的
 *  curricula/(2026-08-28 分学科之前上传的旧文件)。
 *
 *  取「最新」用 sortFilesByTimeDesc 而不是数组最后一个 —— 后者取的是
 *  接口返回顺序,和上传时间无关,换个后端排序就悄悄换一份大纲。 */
export async function resolveAgenda(
  slug: string,
): Promise<{ file: DocTreeFile; path: string; legacy: boolean } | null> {
  if (slug) {
    const scoped = await listDocs(curriculaPathFor(slug)).catch(() => null);
    const hit = sortFilesByTimeDesc(scoped?.files ?? [])[0];
    if (hit) {
      return { file: hit, path: `${curriculaPathFor(slug)}/${hit.name}`, legacy: false };
    }
  }
  const flat = await listDocs(CURRICULA_PATH).catch(() => null);
  const hit = sortFilesByTimeDesc(flat?.files ?? [])[0];
  if (hit) {
    // 有学科却落到平铺目录 = 用的是分学科之前的旧文件,界面要说清楚。
    return { file: hit, path: `${CURRICULA_PATH}/${hit.name}`, legacy: Boolean(slug) };
  }
  return null;
}

function AgendaSection(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 大纲是「某学科的」大纲。多学科老师要能分别上传和查看,否则生成
  // 物理计划时会静默套用数学大纲(Jarod 2026-08-28)。
  const { employees: agendaEmployees } = useMyEmployees();
  const [pickedAgendaSubject, setPickedAgendaSubject] = useState<string>("");
  const agendaSubject = pickedAgendaSubject || (agendaEmployees[0]?.slug ?? "");

  const agenda = useQuery({
    queryKey: ["agenda-file", agendaSubject],
    queryFn: () => resolveAgenda(agendaSubject),
    retry: false,
  });
  const latest = agenda.data ?? null;

  function refresh() {
    void qc.invalidateQueries({ queryKey: ["agenda-file"] });
    void qc.invalidateQueries({ queryKey: ["documents-tree"] });
  }

  async function upload(file: File) {
    setError(null);
    try {
      await uploadDoc(curriculaPathFor(agendaSubject), file.name, file);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // 「从聊天中直接导入」—— Jarod 2026-09-03:聊天里生成的教学大纲产物
  // 已经在「我的知识库/生成产物」里了,这里不该让老师下载再上传一遍。
  // 上传入口保留;导入 = 把 artifacts/ 下选中的文件复制到本学科的大纲目录。
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState<string | null>(null);
  const artifacts = useQuery({
    queryKey: ["documents-tree", "artifacts", "agenda-import"],
    queryFn: () => listDocs("artifacts"),
    enabled: importOpen,
    retry: false,
  });
  const importable = sortFilesByTimeDesc(artifacts.data?.files ?? []).filter((f) =>
    /\.(md|markdown|txt|html?|mhtml|mht|pdf|docx)$/i.test(f.name),
  );

  async function importFromChat(name: string) {
    setError(null);
    setImporting(name);
    try {
      const r = await fetch(`/api/documents/download/${encodeURIComponent(`artifacts/${name}`)}`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`download ${r.status}`);
      const blob = await r.blob();
      await uploadDoc(curriculaPathFor(agendaSubject), name, blob);
      setImportOpen(false);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setImporting(null);
    }
  }

  async function remove() {
    if (!latest) return;
    if (!window.confirm(t("teachingPlans.form.confirmRemoveAgenda", { name: latest.file.name })))
      return;
    setError(null);
    try {
      await deleteDoc(latest.path);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
      <div className="flex items-baseline justify-between mb-1 gap-3">
        <h2 className="text-sm font-semibold text-slate-700">
          {t("teachingPlans.form.agendaHeading")}
        </h2>
        {agendaEmployees.length > 1 && (
          <select
            value={agendaSubject}
            onChange={(e) => setPickedAgendaSubject(e.target.value)}
            className="px-2 py-1 rounded border border-slate-200 text-xs"
          >
            {agendaEmployees.map((emp) => (
              <option key={emp.subject_id} value={emp.slug}>
                {emp.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        {t("teachingPlans.form.agendaHint")}
      </p>
      {latest ? (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-base">📄</span>
          <span className="text-slate-800 flex-1 truncate">
            {latest.file.name}
            {latest.legacy && (
              <span className="ml-2 text-xs text-amber-600">
                {t("teachingPlans.form.agendaLegacy")}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            {t("teachingPlans.form.replaceAgenda")}
          </button>
          <button
            type="button"
            onClick={remove}
            className="text-xs text-rose-600 hover:underline"
          >
            {t("teachingPlans.form.removeAgenda")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs px-3 py-1 rounded bg-sky-50 text-sky-700 hover:bg-sky-100"
        >
          {t("teachingPlans.form.uploadAgenda")}
        </button>
      )}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setImportOpen((v) => !v)}
          title={String(t("teachingPlans.form.importFromChatHint"))}
          className="text-xs px-3 py-1 rounded bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200"
        >
          {t("teachingPlans.form.importFromChat")}
        </button>
        {importOpen && (
          <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 max-h-56 overflow-auto">
            {artifacts.isLoading ? (
              <p className="text-xs text-slate-500">{t("common.loading")}</p>
            ) : importable.length === 0 ? (
              <p className="text-xs text-slate-500">{t("teachingPlans.form.importEmpty")}</p>
            ) : (
              <ul className="space-y-1">
                {importable.slice(0, 30).map((f) => (
                  <li key={f.name} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate text-slate-700" title={f.name}>{f.name}</span>
                    <button
                      type="button"
                      disabled={importing !== null}
                      onClick={() => void importFromChat(f.name)}
                      className="shrink-0 px-2 py-0.5 rounded bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                    >
                      {importing === f.name ? t("teachingPlans.form.importing") : t("teachingPlans.form.importPick")}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="text-xs text-rose-600 mt-2">{error}</p>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".md,.markdown,.txt,.pdf,.docx,.doc,.html,.htm,.mhtml,.mht"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
          e.target.value = "";
        }}
      />
    </section>
  );
}

// TD-6: 无课纲/无 KP 时的学科通用主题建议(不给空白输入框)。
const GENERIC_TOPICS: Record<string, string[]> = {
  english: ["一般现在时与现在进行时", "阅读理解:主旨与细节", "词汇:话题分类记忆", "写作:段落结构", "听力:关键信息捕捉"],
  math: ["有理数运算", "一元一次方程", "几何图形初步", "数据的收集与整理", "分式与因式分解"],
  chinese: ["记叙文阅读", "文言文实词虚词", "作文:细节描写", "古诗词鉴赏", "说明文阅读方法"],
  physics: ["力与运动", "密度与浮力", "光的传播与成像", "电路基础", "能量守恒"],
  default: ["本周单元核心概念", "上次作业共性错题讲评", "期中/期末复习专题", "跨学科拓展主题"],
};

// ── 2. Generate section ──────────────────────────────────────────────

function GenerateSection(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const classes = useClasses();
  const generate = useGenerateTeachingPlan();
  const { employees: planEmployees } = useMyEmployees();
  const [pickedPlanSubject, setPickedPlanSubject] = useState<string>("");
  // 多学科老师也必须默认到第一个学科:select 的 value="" 时浏览器会
  // 显示第一个 option(数学),但内部状态是空 → 主题候选按"无学科"查,
  // 拉回全部 KP(英语六轴)。显示与状态必须一致(Jarod 2026-08-27;
  // 作业页 2026-08-14 修过同款,这里漏了)。
  const planSubject = pickedPlanSubject || (planEmployees[0]?.slug ?? "");

  const [week, setWeek] = useState(1);
  const [topic, setTopic] = useState("");
  const [classId, setClassId] = useState<string>("");
  // TD-6: 主题不再是空白自由输入 —— datalist 候选取现成结构化数据
  // (进度日历单元 + 本学科已启用 KP),无数据时回落学科通用建议。
  const subjects = useSubjects();
  const planSubjectId = subjects.data?.find((sub) => sub.slug === planSubject)?.id;
  const topicUnits = useCurriculumUnits(classId || undefined, undefined, planSubjectId);
  const topicKps = useKnowledgePoints("approved", planSubjectId ?? null);
  const topicOptions = useMemo(() => {
    const out: string[] = [];
    for (const u of topicUnits.data ?? []) if (u.title) out.push(u.title);
    for (const k of topicKps.data ?? []) if (k.name_zh) out.push(k.name_zh);
    if (out.length === 0) {
      out.push(...(GENERIC_TOPICS[planSubject] ?? GENERIC_TOPICS.default ?? []));
    }
    return [...new Set(out)].slice(0, 30);
  }, [topicUnits.data, topicKps.data, planSubject]);
  const [customPrompt, setCustomPrompt] = useState("");
  const [latest, setLatest] = useState<TeachingPlan | null>(null);
  const [savedAs, setSavedAs] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function readAgendaText(): Promise<string | null> {
    try {
      // 喂给 AI 的大纲必须是**本次选中学科**的大纲。这里曾经无视学科、
      // 直接取 curricula/ 里的最后一个文件,于是数学老师生成物理计划
      // 时套的是数学大纲,还一声不吭(Jarod 2026-08-28)。
      const found = await qc.fetchQuery({
        queryKey: ["agenda-file", planSubject],
        queryFn: () => resolveAgenda(planSubject),
        staleTime: 30_000,
      });
      if (!found) return null;
      // 多学科老师 + 未分学科的旧大纲 = 无从判断它是哪一科的,套上去
      // 就是一次静默的张冠李戴。宁可不给大纲(AI 退回按主题和已讲单元
      // 生成),也不喂一份可能属于别科的。单学科老师不受影响 —— 那份
      // 旧文件必然就是他那一科的。
      if (found.legacy && planEmployees.length > 1) return null;
      if (!TEXTUAL_EXTS.test(found.file.name)) {
        return `(The teacher's curriculum agenda is "${found.file.name}" — binary file, content not embedded.)`;
      }
      return await readDocTextForPrompt(found.path, found.file.name);
    } catch {
      return null;
    }
  }

  async function submit() {
    if (!topic.trim()) return;
    setError(null);
    setSavedAs(null);
    try {
      const agendaText = await readAgendaText();
      const picked = classes.data?.find((c) => c.id === classId);
      const className = picked?.name ?? null;
      // v2(2026-09-15):大纲单独作为 syllabus_text 传给后端(后端截断),
      // 学科/班级显式传;不再拼进 custom_prompt。
      const materials = await generate.mutateAsync({
        week,
        topic: topic.trim(),
        subject_slug: planSubject || undefined,
        class_id: classId || null,
        syllabus_text: agendaText,
        custom_prompt: customPrompt.trim() || null,
      });
      setLatest(materials);

      const md = serializeTeachingPlanV2(materials, {
        week,
        topic: topic.trim(),
        className,
        customPrompt: customPrompt.trim() || null,
        generatedAt: new Date(),
      });
      const filename = planFilename({ week, topic: topic.trim() });
      await uploadDoc(PLANS_PATH, filename, new Blob([md], { type: "text/markdown" }));
      setSavedAs(filename);
      void qc.invalidateQueries({ queryKey: ["documents-tree", PLANS_PATH] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      <section className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">
          {t("teachingPlans.generate.heading")}
        </h2>
        <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-3">
          <label className="text-sm">
            <span className="block text-xs text-slate-600 mb-1">
              {t("teachingPlans.form.weekLabel")}
            </span>
            <input
              type="number"
              min={1}
              value={week}
              onChange={(e) => setWeek(Math.max(1, Number(e.target.value) || 1))}
              className="w-full px-2 py-1 rounded border border-slate-200 font-mono"
            />
          </label>
          <label className="text-sm col-span-2">
            <span className="block text-xs text-slate-600 mb-1">
              {t("teachingPlans.form.topicLabel")}
            </span>
            <input
              type="text"
              list="tp-topic-options"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("teachingPlans.form.topicPlaceholder")}
              className="w-full px-2 py-1 rounded border border-slate-200"
            />
            <datalist id="tp-topic-options">
              {topicOptions.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </label>
          {planEmployees.length > 1 && (
            <label className="text-sm col-span-3">
              <span className="block text-xs text-slate-600 mb-1">{t("exams.create.subjectLabel")}</span>
              <select
                value={planSubject}
                onChange={(e) => setPickedPlanSubject(e.target.value)}
                className="w-full px-2 py-1 rounded border border-slate-200"
              >
                {planEmployees.map((emp: { slug: string; name: string }) => (
                  <option key={emp.slug} value={emp.slug}>{emp.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm col-span-3">
            <span className="block text-xs text-slate-600 mb-1">
              {t("teachingPlans.form.classLabel")}
            </span>
            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              className="w-full px-2 py-1 rounded border border-slate-200"
            >
              <option value="">{t("teachingPlans.form.classNone")}</option>
              {classes.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mb-3">
          <CustomPromptInput
            value={customPrompt}
            onChange={setCustomPrompt}
            placeholder={t("teachingPlans.form.customPromptPlaceholder")}
            surface="extension"
            context={{ week, topic , subject: planSubject || undefined}}
          />
        </div>
        <div className="flex justify-end items-center gap-3">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <button
            type="button"
            onClick={submit}
            disabled={!topic.trim() || generate.isPending}
            className="px-4 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
          >
            {generate.isPending
              ? t("teachingPlans.form.generating")
              : t("teachingPlans.form.generate")}
          </button>
        </div>
        {savedAs && (
          <p className="text-xs text-emerald-700 mt-3">
            {t("teachingPlans.generate.savedAs", { filename: savedAs })}
          </p>
        )}
      </section>

      {latest && (
        <>
          <div className="mb-3 flex justify-end">
            <Link
              to={`/exams?create=1&class_id=${classId}&title=${encodeURIComponent(t("teachingPlans.examCta.title", { week, topic: topic.trim() }))}`}
              className="px-3 py-1.5 rounded bg-violet-500 text-white text-sm font-medium hover:bg-violet-600"
            >
              {t("teachingPlans.examCta.button")} →
            </Link>
          </div>
          <InlinePlan data={latest} />
        </>
      )}
    </>
  );
}

// ── 2b. Inline plan display (v2, subject-neutral) ───────────────────

function InlinePlan({ data }: { data: TeachingPlan }): JSX.Element {
  const { t } = useTranslation();
  const card = "rounded-lg border border-slate-200 bg-white p-5";
  const h = "text-sm font-semibold text-slate-800 mb-2";
  return (
    <div className="space-y-4 mb-6" data-testid="teaching-plan-v2">
      <section className={card}>
        <h3 className={h}>{t("teachingPlans.plan.objectives")}</h3>
        <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-1">
          {data.objectives.map((o, i) => <li key={i}><MathText text={o} /></li>)}
        </ol>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {data.knowledge_points.map((k) => (
            <span key={k} className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">{k}</span>
          ))}
        </div>
      </section>
      <section className={card}>
        <h3 className={h}>{t("teachingPlans.plan.lessons")}</h3>
        <div className="space-y-3">
          {data.lessons.map((les) => (
            <div key={les.period} className="rounded bg-slate-50 border border-slate-200 p-3">
              <div className="text-sm font-medium text-slate-800">{t("teachingPlans.plan.period", { n: les.period })} · <MathText text={les.focus} /></div>
              <ul className="mt-1 list-disc pl-5 text-sm text-slate-600 space-y-0.5">
                {les.activities.map((a, i) => <li key={i}><MathText text={a} /></li>)}
                {les.materials && <li className="text-slate-500">{t("teachingPlans.plan.materials")}:{les.materials}</li>}
              </ul>
            </div>
          ))}
        </div>
      </section>
      <section className={card}>
        <h3 className={h}>{t("teachingPlans.plan.exercises", { count: data.exercises.length })}</h3>
        <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-2">
          {data.exercises.map((e, i) => (
            <li key={i}>
              <MathText text={e.stem} />
              <div className="text-xs text-emerald-700 mt-0.5">{t("teachingPlans.plan.answer")}:<MathText text={e.answer} /></div>
            </li>
          ))}
        </ol>
      </section>
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 text-sm text-slate-700">
          <div className="text-xs font-bold text-slate-700 mb-1">{t("teachingPlans.plan.basic")}</div>
          <MathText text={data.differentiation.basic} />
        </div>
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-sm text-slate-700">
          <div className="text-xs font-bold text-slate-700 mb-1">{t("teachingPlans.plan.advanced")}</div>
          <MathText text={data.differentiation.advanced} />
        </div>
      </section>
      <section className={card}>
        <div className="text-sm text-slate-700"><b>{t("teachingPlans.plan.homework")}:</b> <MathText text={data.homework} /></div>
        <div className="text-sm text-slate-700 mt-2"><b>{t("teachingPlans.plan.assessment")}:</b> <MathText text={data.assessment} /></div>
        {data.notes && <p className="text-xs text-slate-500 mt-2">{data.notes}</p>}
      </section>
    </div>
  );
}

// ── 3. Saved-plans list ──────────────────────────────────────────────

function PlansListSection(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const tree = useQuery({
    queryKey: ["documents-tree", PLANS_PATH],
    queryFn: () => listDocs(PLANS_PATH),
    retry: false,
  });

  const remove = useMutation({
    mutationFn: (filename: string) => deleteDoc(`${PLANS_PATH}/${filename}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents-tree", PLANS_PATH] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
    },
  });

  const rows = useMemo(() => {
    const files = tree.data?.files ?? [];
    return files
      .map((f) => ({ name: f.name, ...parsePlanFilename(f.name) }))
      .sort((a, b) => {
        if (a.week === null && b.week === null) return a.name.localeCompare(b.name);
        if (a.week === null) return 1;
        if (b.week === null) return -1;
        return a.week - b.week;
      });
  }, [tree.data]);

  function askDelete(name: string) {
    if (!window.confirm(t("teachingPlans.edit.confirmDelete", { name }))) return;
    remove.mutate(name);
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">
        {t("teachingPlans.list.title")}
      </h2>
      {tree.isLoading && (
        <p className="text-xs text-slate-500 italic">{t("common.loading")}</p>
      )}
      {tree.error && (
        <p className="text-xs text-rose-600">
          {(tree.error as Error).message}
        </p>
      )}
      {!tree.isLoading && rows.length === 0 && !tree.error && (
        <p className="text-xs text-slate-500 italic">
          {t("teachingPlans.list.empty")}
        </p>
      )}
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-2 w-16">{t("teachingPlans.list.weekColumn")}</th>
              <th className="py-2">{t("teachingPlans.list.topicColumn")}</th>
              <th className="py-2 text-right w-44"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 font-mono text-slate-700">
                  {r.week !== null ? r.week : "—"}
                </td>
                <td className="py-2 text-slate-800">
                  {r.topicSlug.replace(/-/g, " ")}
                </td>
                <td className="py-2 text-right">
                  <Link
                    to={`/exams?create=1&title=${encodeURIComponent(t("teachingPlans.examCta.title", { week: r.week ?? "", topic: r.topicSlug.replace(/-/g, " ") }))}`}
                    className="text-xs px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100 mr-1"
                  >
                    {t("teachingPlans.examCta.button")}
                  </Link>
                  <Link
                    to={`/teaching-plans/${encodeURIComponent(r.name)}`}
                    className="text-xs px-2 py-1 rounded bg-sky-50 text-sky-700 hover:bg-sky-100 mr-1"
                  >
                    {t("common.view")}
                  </Link>
                  <button
                    type="button"
                    onClick={() => askDelete(r.name)}
                    disabled={remove.isPending}
                    className="text-xs px-2 py-1 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                  >
                    {t("teachingPlans.edit.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* navigate import kept for future use (e.g. View page); silence lint. */}
      <span hidden onClick={() => navigate("/teaching-plans")} />
    </section>
  );
}
